import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, AlertTriangle } from 'lucide-react';
import { useMatter } from '../hooks/useMatter';
import { fetchDeadlines, bucketBySeverity, type Deadline } from '../lib/deadlines';
import DeadlinesList from '../components/DeadlinesList';

export default function Deadlines() {
  const { isStaff } = useMatter();
  const [items, setItems] = useState<Deadline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const all = await fetchDeadlines();
        if (!cancelled) setItems(all);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load deadlines');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // For MVP: show all matters for all staff. Per-staff filtering is post-MVP
  // (requires assigned_staff_id on each deadline row).
  const filtered = items;

  if (!isStaff) {
    return (
      <div className="card p-6 text-sm text-slate-600">
        Deadlines view is for staff only.
      </div>
    );
  }

  const buckets = bucketBySeverity(filtered);
  const counts = {
    critical: buckets.critical.length,
    attention: buckets.attention.length,
    upcoming: buckets.upcoming.length,
  };

  return (
    <div className="space-y-5">
      <div>
        <Link to="/dashboard" className="text-xs text-slate-500 hover:text-[#0B2341] inline-flex items-center gap-1">
          <ChevronLeft size={12} /> Dashboard
        </Link>
        <h1 className="text-2xl font-bold text-[#0B2341] mt-2">Deadlines</h1>
        <p className="text-sm text-slate-500 mt-1">
          Visa expiries, lodgement targets, and RFI deadlines across all matters in the next 30 days.
        </p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 max-w-xl">
        <StatCard label="Critical" count={counts.critical} tone="red" />
        <StatCard label="Attention" count={counts.attention} tone="amber" />
        <StatCard label="Upcoming" count={counts.upcoming} tone="slate" />
      </div>

      {/*
        "My matters" filter is post-MVP (requires per-matter assigned_staff_id
        on each deadline row). Removed the toggle to avoid giving false
        confidence to staff that they're seeing only their own work. We currently
        show all matters for all staff; admins want this anyway for triage.
      */}

      {error && (
        <div className="card p-4 text-sm text-red-600 inline-flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading deadlines...</div>
      ) : (
        <div className="card p-4">
          <DeadlinesList items={filtered} emptyMessage="Nothing on the radar in the next 30 days." />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, count, tone }: { label: string; count: number; tone: 'red' | 'amber' | 'slate' }) {
  const toneCls =
    tone === 'red' && count > 0
      ? 'text-red-700'
      : tone === 'amber' && count > 0
      ? 'text-amber-700'
      : 'text-[#0B2341]';
  return (
    <div className="card p-3">
      <div className={`text-2xl font-bold ${toneCls}`}>{count}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

